import cx from 'classnames'
import { Link } from 'features/i18n/router'
import { useEffect, useRef, useState } from 'react'

import { useDetectClickOutside } from 'hooks'

export type MenuOption = {
  id?: string
  title: string | JSX.Element
  to?: string
  onClick?: () => void
  style?: object
}

const Menu = ({
  isActive,
  onToggle,
  options,
  closeAfterClicked = true,
  icon = 'dropdown',
  handlerClassName = '',
  handlerElement,
  arrowPosition = 'top',
  panelPosition = 'left',
  hideArrow,
  theme = 'black',
  openDownwards,
}: {
  isActive?: boolean
  onToggle?: Function
  options: Array<MenuOption>
  closeAfterClicked?: boolean
  icon?: 'dropdown' | 'ellipsis' | 'ellipsis-white' | '3dots-v' | 'search-white'
  arrowPosition?: 'left' | 'right' | 'top'
  panelPosition?: 'left' | 'right' | 'down' | 'left-down'
  handlerClassName?: string
  handlerElement?: ({
    onClick,
    isMenuOpen,
  }: {
    onClick: Function
    isMenuOpen: boolean
  }) => void
  hideArrow?: boolean
  theme?: 'white' | 'black'
  openDownwards?: boolean
}) => {
  const [isMenuOpen, setMenuOpen] = useState(isActive || false)
  const menuRef = useRef(null)

  useDetectClickOutside(menuRef, () => setMenuOpen(false), isMenuOpen)

  useEffect(() => {
    if (isActive !== isMenuOpen) {
      setMenuOpen(isActive)
    }
  }, [isActive])

  const onToggleMenu = (e: any) => {
    setMenuOpen(!isMenuOpen)
    if (typeof onToggle === 'function') {
      onToggle(!isMenuOpen, e)
    }
  }

  const onListClicked = (cb: Function) => {
    if (closeAfterClicked) {
      setMenuOpen(false)
    }
    cb()
  }

  return (
    <div
      className={cx(
        'Menu',
        openDownwards && 'Menu--open-downwards',
        isMenuOpen && 'Menu--active',
        `Menu--${theme}`
      )}
      ref={menuRef}
    >
      {handlerElement ? (
        handlerElement({ onClick: onToggleMenu, isMenuOpen })
      ) : (
        <a
          className={`Menu__handle Menu__handle--${icon} ${handlerClassName}`}
          onClick={onToggleMenu}
        />
      )}

      <div
        className={cx(
          'Menu__panel',
          `Menu__panel--${panelPosition}`,
          `Menu__panel--arrow-${arrowPosition}`,
          hideArrow && 'Menu__panel--no-arrow'
        )}
      >
        <ul className="Menu__panel__ul">
          {options.map((option, i) => {
            if (typeof option.to !== 'undefined') {
              return (
                <li
                  key={typeof option.title === 'string' ? option.title : i}
                  style={option.style}
                >
                  <Link
                    className="Menu__panel__ul__a"
                    to={option.to}
                    onClick={() =>
                      onListClicked(() =>
                        option.onClick ? onToggle ?? option.onClick : () => {}
                      )
                    }
                  >
                    {option.title}
                  </Link>
                </li>
              )
            }

            return (
              <li key={typeof option.title === 'string' ? option.title : i}>
                <a
                  className="Menu__panel__ul__a"
                  onClick={(e) => {
                    e.preventDefault()
                    onListClicked(option.onClick)
                  }}
                >
                  {option.title}
                </a>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export default Menu
